import type { DraftSegment, EdgeDefinition, PointReference, PointTuple } from "./types";

const CURVE_OPERATIONS = new Set(["bezierCurve", "tangentCurve", "armholeCurve", "hollowCurve", "sleeveHeadCurve", "bentCurve", "forkCurve"]);

/**
 * Every edge operation. `edgePortion`, `mirrorEdge` and `transformEdge` are
 * resolved by the draft from another drafted edge.
 */
export const EDGE_OPERATIONS: ReadonlySet<string> = new Set(["line", "edgePortion", "mirrorEdge", "transformEdge", ...CURVE_OPERATIONS]);

/** Edge operations drafted from another drafted edge rather than from points alone. */
export const DERIVED_EDGE_OPERATIONS: ReadonlySet<string> = new Set(["edgePortion", "mirrorEdge", "transformEdge"]);

export function compileEdge(edge: EdgeDefinition, points: Record<string, PointTuple>): DraftSegment[] {
  if (DERIVED_EDGE_OPERATIONS.has(edge.operation)) throw new Error(`${edge.operation} needs its source edge drafted first`);
  const start = requirePoint(edge.from, points);
  const end = requirePoint(edge.to, points);
  if (!CURVE_OPERATIONS.has(edge.operation)) return [{ kind: "line", start, end }];

  if (edge.operation === "bezierCurve") {
    return [{ kind: "bezier", start, end, control1: resolveControl(edge.control1, start, points), control2: resolveControl(edge.control2, end, points) }];
  }
  if (edge.operation === "bentCurve") return [bentSegment(start, end, edge.bend ?? 0)];
  if (edge.operation === "forkCurve") {
    const toward = requirePoint(edge.toward ?? "", points);
    const touch = edge.touch ? requirePoint(edge.touch, points) : undefined;
    return [forkSegment(start, end, toward, touch, edge.style ?? "front")];
  }

  const along = (pair: [string, string] | undefined): PointTuple | undefined => {
    if (!pair) return undefined;
    const [from, to] = pair.map((id) => requirePoint(id, points));
    return unit([to[0] - from[0], to[1] - from[1]]);
  };
  const startAlong = along(edge.constraints?.startAlong);
  const endAlong = along(edge.constraints?.endAlong);
  const through = (edge.through ?? []).map((id) => requirePoint(id, points));
  const segments = through.length
    ? constrainSplineEnds(splineToBezier([start, ...through, end], edge.tension ?? defaultTension(edge.operation), edge.endHandles ?? "short"), edge.constraints)
    : [tangentSegment(start, end, edge.constraints, edge.reverse ?? false)];
  return alignEnds(segments, startAlong, endAlong, edge.constraints);
}

/** Handle presets from the v1 jeans fork curves (millimetres and ratios). */
const FORK_STYLES = {
  front: { horizontalScale: 1.1, minHorizontal: 38, maxHorizontal: 82, endScale: 0.42 },
  back: { horizontalScale: 1, minHorizontal: 50, maxHorizontal: 110, endScale: 0.45 },
} as const;
const FORK_MIN_END_HANDLE = 42;
const FORK_MAX_END_HANDLE = 125;

/**
 * A crotch curve: it leaves the fork point horizontally and arrives at `end`
 * already heading toward `toward`, so it runs smoothly into the rise line.
 */
function forkSegment(start: PointTuple, end: PointTuple, toward: PointTuple, touch: PointTuple | undefined, style: "front" | "back"): DraftSegment {
  const preset = FORK_STYLES[style];
  const reach = (touch ?? end)[0] - start[0];
  const horizontal = clamp(Math.abs(reach) * preset.horizontalScale, preset.minHorizontal, preset.maxHorizontal);
  const direction = unit([toward[0] - end[0], toward[1] - end[1]]);
  const endHandle = clamp(Math.hypot(toward[0] - end[0], toward[1] - end[1]) * preset.endScale, FORK_MIN_END_HANDLE, FORK_MAX_END_HANDLE);
  return {
    kind: "bezier",
    start,
    control1: [start[0] + (Math.sign(reach) || 1) * horizontal, start[1]],
    control2: [end[0] - direction[0] * endHandle, end[1] - direction[1] * endHandle],
    end,
  };
}

function tangentSegment(start: PointTuple, end: PointTuple, constraints: EdgeDefinition["constraints"], reverse: boolean): DraftSegment {
  const distance = Math.hypot(end[0] - start[0], end[1] - start[1]) / 3;
  const direction = (kind: "horizontal" | "vertical" | undefined, fallback: PointTuple): PointTuple =>
    kind === "horizontal" ? [Math.sign(fallback[0]) || 1, 0] : kind === "vertical" ? [0, Math.sign(fallback[1]) || 1] : unit(fallback);
  const chord: PointTuple = [end[0] - start[0], end[1] - start[1]];
  const sign = reverse ? -1 : 1;
  const startDirection = rotate(
    direction(constraints?.startTangent, chord).map((value) => value * sign) as PointTuple,
    constraints?.startTangentRotation ?? 0,
  );
  const endDirection = rotate(
    direction(constraints?.endTangent, chord).map((value) => value * sign) as PointTuple,
    constraints?.endTangentRotation ?? 0,
  );
  return { kind: "bezier", start, end, control1: [start[0] + startDirection[0] * distance, start[1] + startDirection[1] * distance], control2: [end[0] - endDirection[0] * distance, end[1] - endDirection[1] * distance] };
}

function bentSegment(start: PointTuple, end: PointTuple, bend: number): DraftSegment {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const length = Math.hypot(dx, dy) || 1;
  const normal: PointTuple = [-dy / length, dx / length];
  const control: PointTuple = [(start[0] + end[0]) / 2 + normal[0] * bend * 2, (start[1] + end[1]) / 2 + normal[1] * bend * 2];
  return { kind: "bezier", start, end, control1: mix(start, control, 2 / 3), control2: mix(end, control, 2 / 3) };
}

/** Catmull-Rom interpolation converted to cubic Bézier segments. */
export function splineToBezier(points: PointTuple[], tension = 0, endHandles: "short" | "natural" = "short"): DraftSegment[] {
  const tangentScale = (1 - clamp(tension, 0, 1)) / 6;
  // "natural" reflects the neighbour through each end, as if the curve carried on.
  const reflect = (origin: PointTuple, point: PointTuple): PointTuple => [origin[0] * 2 - point[0], origin[1] * 2 - point[1]];
  return points.slice(0, -1).map((start, index) => {
    const end = points[index + 1];
    const previous = index > 0 ? points[index - 1] : endHandles === "natural" ? reflect(start, end) : start;
    const next = index + 2 < points.length ? points[index + 2] : endHandles === "natural" ? reflect(end, start) : end;
    return { kind: "bezier", start, end, control1: [start[0] + (end[0] - previous[0]) * tangentScale, start[1] + (end[1] - previous[1]) * tangentScale], control2: [end[0] - (next[0] - start[0]) * tangentScale, end[1] - (next[1] - start[1]) * tangentScale] };
  });
}

export function sampleSegment(segment: DraftSegment, steps = 20): PointTuple[] {
  if (segment.kind === "line") return [segment.start, segment.end];
  return Array.from({ length: steps + 1 }, (_, index) => {
    const t = index / steps;
    const mt = 1 - t;
    return [mt ** 3 * segment.start[0] + 3 * mt ** 2 * t * segment.control1[0] + 3 * mt * t ** 2 * segment.control2[0] + t ** 3 * segment.end[0], mt ** 3 * segment.start[1] + 3 * mt ** 2 * t * segment.control1[1] + 3 * mt * t ** 2 * segment.control2[1] + t ** 3 * segment.end[1]];
  });
}

/** Turn the outer handles to follow given directions, keeping their lengths. */
function alignEnds(segments: DraftSegment[], startAlong: PointTuple | undefined, endAlong: PointTuple | undefined, constraints: EdgeDefinition["constraints"]): DraftSegment[] {
  const first = segments[0];
  const last = segments[segments.length - 1];
  if (startAlong && first?.kind === "bezier") {
    const length = Math.hypot(first.control1[0] - first.start[0], first.control1[1] - first.start[1]) * Math.max(0, constraints?.startTangentStrength ?? 1);
    first.control1 = [first.start[0] + startAlong[0] * length, first.start[1] + startAlong[1] * length];
  }
  if (endAlong && last?.kind === "bezier") {
    const length = Math.hypot(last.control2[0] - last.end[0], last.control2[1] - last.end[1]) * Math.max(0, constraints?.endTangentStrength ?? 1);
    last.control2 = [last.end[0] - endAlong[0] * length, last.end[1] - endAlong[1] * length];
  }
  return segments;
}

function constrainSplineEnds(segments: DraftSegment[], constraints: EdgeDefinition["constraints"]): DraftSegment[] {
  if (!segments.length || !constraints) return segments;
  const first = segments[0];
  const last = segments[segments.length - 1];
  if (first.kind === "bezier" && constraints.startTangent) {
    first.control1 = constrainedControl(first.start, first.control1, constraints.startTangent, constraints.startTangentStrength ?? 1, constraints.startTangentRotation ?? 0);
  }
  if (last.kind === "bezier" && constraints.endTangent) {
    last.control2 = constrainedControl(last.end, last.control2, constraints.endTangent, constraints.endTangentStrength ?? 1, constraints.endTangentRotation ?? 0);
  }
  return segments;
}

function constrainedControl(anchor: PointTuple, control: PointTuple, tangent: "horizontal" | "vertical", strength: number, rotation: number): PointTuple {
  const length = Math.hypot(control[0] - anchor[0], control[1] - anchor[1]) * Math.max(0, strength);
  const vector: PointTuple = tangent === "horizontal"
    ? [(Math.sign(control[0] - anchor[0]) || -1) * length, 0]
    : [0, (Math.sign(control[1] - anchor[1]) || -1) * length];
  const rotated = rotate(vector, rotation);
  return [anchor[0] + rotated[0], anchor[1] + rotated[1]];
}

function rotate(vector: PointTuple, degrees: number): PointTuple {
  const radians = degrees * Math.PI / 180;
  return [
    vector[0] * Math.cos(radians) - vector[1] * Math.sin(radians),
    vector[0] * Math.sin(radians) + vector[1] * Math.cos(radians),
  ];
}

function resolveControl(reference: PointReference | undefined, anchor: PointTuple, points: Record<string, PointTuple>): PointTuple {
  if (typeof reference === "string") return requirePoint(reference, points);
  if (Array.isArray(reference)) return reference;
  if (!reference) throw new Error("bezierCurve requires control1 and control2");
  const base = reference.point ? requirePoint(reference.point, points) : anchor;
  return [reference.x ?? base[0] + (reference.dx ?? 0), reference.y ?? base[1] + (reference.dy ?? 0)];
}
function requirePoint(id: string, points: Record<string, PointTuple>): PointTuple { const point = points[id]; if (!point) throw new Error(`Point "${id}" is not available`); return point; }
function unit(point: PointTuple): PointTuple { const length = Math.hypot(...point) || 1; return [point[0] / length, point[1] / length]; }
function mix(a: PointTuple, b: PointTuple, amount: number): PointTuple { return [a[0] + (b[0] - a[0]) * amount, a[1] + (b[1] - a[1]) * amount]; }
function clamp(value: number, min: number, max: number): number { return Math.max(min, Math.min(max, value)); }
function defaultTension(operation: string): number { return operation === "hollowCurve" ? 0.15 : 0; }

/** Split a cubic at t with de Casteljau, returning the two halves. */
function splitBezier(segment: Extract<DraftSegment, { kind: "bezier" }>, t: number): [DraftSegment, DraftSegment] {
  const a = mix(segment.start, segment.control1, t);
  const b = mix(segment.control1, segment.control2, t);
  const c = mix(segment.control2, segment.end, t);
  const d = mix(a, b, t);
  const e = mix(b, c, t);
  const f = mix(d, e, t);
  return [
    { kind: "bezier", start: segment.start, control1: a, control2: d, end: f },
    { kind: "bezier", start: f, control1: e, control2: c, end: segment.end },
  ];
}

function pointAt(segment: DraftSegment, t: number): PointTuple {
  if (segment.kind === "line") return mix(segment.start, segment.end, t);
  const [first] = splitBezier(segment, t);
  return first.end;
}

/** The segment index and parameter of the point on `segments` nearest to `target`. */
function nearestParameter(segments: DraftSegment[], target: PointTuple): { index: number; t: number; distance: number } {
  let best = { index: 0, t: 0, distance: Number.POSITIVE_INFINITY };
  const distanceAt = (index: number, t: number) => {
    const point = pointAt(segments[index], t);
    return Math.hypot(point[0] - target[0], point[1] - target[1]);
  };
  // Refine around each segment's best sample: a point just past a joint can
  // sample closest to the previous segment's end and only resolve on the next.
  segments.forEach((_, index) => {
    const steps = 200;
    let sample = 0;
    let sampleDistance = Number.POSITIVE_INFINITY;
    for (let step = 0; step <= steps; step += 1) {
      const distance = distanceAt(index, step / steps);
      if (distance < sampleDistance) [sample, sampleDistance] = [step / steps, distance];
    }
    let low = Math.max(0, sample - 1 / steps);
    let high = Math.min(1, sample + 1 / steps);
    for (let iteration = 0; iteration < 40; iteration += 1) {
      const a = low + (high - low) / 3;
      const b = high - (high - low) / 3;
      if (distanceAt(index, a) < distanceAt(index, b)) high = b;
      else low = a;
    }
    const t = (low + high) / 2;
    const distance = distanceAt(index, t);
    if (distance < best.distance) best = { index, t, distance };
  });
  return best;
}

function sliceSegment(segment: DraftSegment, from: number, to: number): DraftSegment {
  if (segment.kind === "line") return { kind: "line", start: mix(segment.start, segment.end, from), end: mix(segment.start, segment.end, to) };
  const [, tail] = splitBezier(segment, from);
  if (tail.kind !== "bezier") return tail;
  const local = from >= 1 ? 0 : (to - from) / (1 - from);
  return splitBezier(tail, Math.min(1, Math.max(0, local)))[0];
}

export function reverseSegments(segments: DraftSegment[]): DraftSegment[] {
  return [...segments].reverse().map((segment) => segment.kind === "line"
    ? { kind: "line", start: segment.end, end: segment.start }
    : { kind: "bezier", start: segment.end, control1: segment.control2, control2: segment.control1, end: segment.start });
}

/**
 * An edge reflected in the line through `axisStart` and `axisEnd`, running
 * from `from` to `to`, which must be the mirror images of the source's ends
 * (in either order). Throws if they are more than `tolerance` mm off.
 */
export function mirrorSegments(segments: DraftSegment[], axisStart: PointTuple, axisEnd: PointTuple, from: PointTuple, to: PointTuple, tolerance = 0.5): DraftSegment[] {
  const dx = axisEnd[0] - axisStart[0];
  const dy = axisEnd[1] - axisStart[1];
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared < 1e-12) throw new Error("A mirror axis needs two distinct points");
  const reflect = ([x, y]: PointTuple): PointTuple => {
    const t = ((x - axisStart[0]) * dx + (y - axisStart[1]) * dy) / lengthSquared;
    const px = axisStart[0] + t * dx;
    const py = axisStart[1] + t * dy;
    return [2 * px - x, 2 * py - y];
  };
  let mirrored: DraftSegment[] = segments.map((segment) => segment.kind === "line"
    ? { kind: "line", start: reflect(segment.start), end: reflect(segment.end) }
    : { kind: "bezier", start: reflect(segment.start), control1: reflect(segment.control1), control2: reflect(segment.control2), end: reflect(segment.end) });
  const gap = (a: PointTuple, b: PointTuple) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const first = mirrored[0].start;
  const last = mirrored[mirrored.length - 1].end;
  if (gap(first, to) <= tolerance && gap(last, from) <= tolerance && !(gap(first, from) <= tolerance && gap(last, to) <= tolerance)) mirrored = reverseSegments(mirrored);
  if (gap(mirrored[0].start, from) > tolerance || gap(mirrored[mirrored.length - 1].end, to) > tolerance) {
    throw new Error("A mirrored edge's end points are not the mirror images of its source's ends");
  }
  // Snap the ends exactly onto the requested points.
  mirrored[0] = { ...mirrored[0], start: from } as DraftSegment;
  mirrored[mirrored.length - 1] = { ...mirrored[mirrored.length - 1], end: to } as DraftSegment;
  return mirrored;
}

/**
 * The stretch of an edge between two points on it, running from `from` to
 * `to`. Throws if either point is more than `tolerance` mm off the edge.
 */
export function edgePortion(segments: DraftSegment[], from: PointTuple, to: PointTuple, tolerance = 0.5): DraftSegment[] {
  const start = nearestParameter(segments, from);
  const end = nearestParameter(segments, to);
  if (start.distance > tolerance || end.distance > tolerance) throw new Error("A portion's end point is not on its edge");
  const forward = start.index < end.index || (start.index === end.index && start.t <= end.t);
  const [first, last] = forward ? [start, end] : [end, start];
  const pieces: DraftSegment[] = [];
  for (let index = first.index; index <= last.index; index += 1) {
    const from = index === first.index ? first.t : 0;
    const to = index === last.index ? last.t : 1;
    if (to - from > 1e-9) pieces.push(sliceSegment(segments[index], from, to));
  }
  // Snap the ends exactly onto the requested points.
  if (pieces.length) {
    pieces[0] = { ...pieces[0], start: forward ? from : to } as DraftSegment;
    const lastPiece = pieces[pieces.length - 1];
    pieces[pieces.length - 1] = { ...lastPiece, end: forward ? to : from } as DraftSegment;
  }
  return forward ? pieces : reverseSegments(pieces);
}

/** Join edges end to end into one run, turning each to continue from the last. Undefined if they do not chain. */
export function chainEdges(edges: DraftSegment[][]): DraftSegment[] | undefined {
  if (edges.length === 0) return undefined;
  const same = (a: PointTuple, b: PointTuple) => Math.hypot(a[0] - b[0], a[1] - b[1]) < 0.01;
  let run = edges[0];
  if (edges.length > 1) {
    const next = edges[1];
    const end = run[run.length - 1].end;
    if (!same(end, next[0].start) && !same(end, next[next.length - 1].end)) run = reverseSegments(run);
  }
  const chained = [...run];
  for (const segments of edges.slice(1)) {
    const end = chained[chained.length - 1].end;
    const oriented = same(end, segments[0].start) ? segments : same(end, segments[segments.length - 1].end) ? reverseSegments(segments) : undefined;
    if (!oriented) return undefined;
    chained.push(...oriented);
  }
  return chained;
}

/**
 * Walk `distance` mm along edges chained from `start` (each edge is turned to
 * continue from where the last one ended) and return the point reached.
 */
/**
 * Where the edges, taken in order, first cross the vertical line at `value`
 * (axis "x") or the horizontal line at `value` (axis "y"). Throws if they
 * never do.
 */
export function edgeCrossing(edges: DraftSegment[][], axis: "x" | "y", value: number): PointTuple {
  const index = axis === "x" ? 0 : 1;
  for (const segments of edges) {
    for (const segment of segments) {
      const samples = sampleSegment(segment, segment.kind === "bezier" ? 400 : 1);
      for (let step = 1; step < samples.length; step += 1) {
        const a = samples[step - 1][index] - value;
        const b = samples[step][index] - value;
        if (a === 0) return samples[step - 1];
        if ((a < 0) !== (b < 0) || b === 0) return mix(samples[step - 1], samples[step], a / (a - b));
      }
    }
  }
  throw new Error(`The edges do not cross ${axis} = ${value}`);
}

/**
 * Where the edges, taken in order, first cross any of the `others`. Throws
 * if they never do.
 */
export function edgesCross(edges: DraftSegment[][], others: DraftSegment[][]): PointTuple {
  const sample = (segments: DraftSegment[]) => segments.flatMap((segment, index) => sampleSegment(segment, segment.kind === "bezier" ? 400 : 1).slice(index === 0 ? 0 : 1));
  const targets = others.map(sample);
  for (const path of edges.map(sample)) {
    for (let step = 1; step < path.length; step += 1) {
      const [a, b] = [path[step - 1], path[step]];
      let best: number | undefined;
      for (const target of targets) {
        for (let index = 1; index < target.length; index += 1) {
          const [c, d] = [target[index - 1], target[index]];
          const denominator = (b[0] - a[0]) * (d[1] - c[1]) - (b[1] - a[1]) * (d[0] - c[0]);
          if (denominator === 0) continue;
          const t = ((c[0] - a[0]) * (d[1] - c[1]) - (c[1] - a[1]) * (d[0] - c[0])) / denominator;
          const u = ((c[0] - a[0]) * (b[1] - a[1]) - (c[1] - a[1]) * (b[0] - a[0])) / denominator;
          if (t >= 0 && t <= 1 && u >= 0 && u <= 1 && (best === undefined || t < best)) best = t;
        }
      }
      if (best !== undefined) return mix(a, b, best);
    }
  }
  throw new Error("The edges do not cross");
}

export function pointAlongEdges(edges: DraftSegment[][], start: PointTuple, distance: number): PointTuple {
  const same = (a: PointTuple, b: PointTuple) => Math.hypot(a[0] - b[0], a[1] - b[1]) < 0.01;
  let cursor = start;
  let remaining = distance;
  for (const segments of edges) {
    const oriented = same(segments[0].start, cursor) ? segments : same(segments[segments.length - 1].end, cursor) ? reverseSegments(segments) : undefined;
    if (!oriented) throw new Error("Edges do not chain from the start point");
    for (const segment of oriented) {
      const samples = sampleSegment(segment, segment.kind === "bezier" ? 200 : 1);
      for (let index = 1; index < samples.length; index += 1) {
        const step = Math.hypot(samples[index][0] - samples[index - 1][0], samples[index][1] - samples[index - 1][1]);
        if (step >= remaining) return mix(samples[index - 1], samples[index], step === 0 ? 0 : remaining / step);
        remaining -= step;
      }
    }
    cursor = oriented[oriented.length - 1].end;
  }
  throw new Error("The path is shorter than the requested distance");
}

